export type BatchStatus =
  | 'DRAFT' | 'GENERATED' | 'SENT' | 'CONFIRMED' | 'FAILED' | 'CANCELLED'

export type SettlementStatus =
  | 'DRAFT' | 'CALCULATED' | 'APPROVED' | 'PAID' | 'CANCELLED'

export type LeavingReason =
  | 'RESIGNATION' | 'TERMINATION' | 'END_OF_CONTRACT' | 'RETIREMENT' | 'DEATH' | 'REDUNDANCY'

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  DRAFT: 'Built, no file yet',
  GENERATED: 'File ready to send',
  SENT: 'Uploaded to the bank',
  CONFIRMED: 'Confirmed by the bank',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
}

export const SETTLEMENT_STATUS_LABELS: Record<SettlementStatus, string> = {
  DRAFT: 'Raised, not calculated',
  CALCULATED: 'Awaiting approval',
  APPROVED: 'Approved for payment',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
}

export const LEAVING_REASON_LABELS: Record<LeavingReason, string> = {
  RESIGNATION: 'Resigned',
  TERMINATION: 'Terminated by the employer',
  END_OF_CONTRACT: 'Contract ended',
  RETIREMENT: 'Retired',
  DEATH: 'Died in service',
  REDUNDANCY: 'Redundancy',
}

export const SETTLEMENT_COMPONENT_LABELS: Record<string, string> = {
  GRATUITY: 'End-of-service gratuity',
  UNUSED_LEAVE: 'Unused leave',
  AIR_TICKET: 'Air ticket',
  NOTICE_PAY: 'Notice pay',
  FINAL_SALARY: 'Final salary',
  LOAN_RECOVERY: 'Loan recovery',
  EXPENSE_REIMBURSEMENT: 'Expenses owed',
  ASSET_RECOVERY: 'Asset recovery',
  OTHER: 'Other',
}

export interface WpsLayoutView {
  id: string
  legalEntityId: string | null
  code: string
  name: string
  bankName: string | null
  recordFormat: string
  delimiter: string | null
  recordKind: string
  lineEnding: string
  characterSet: string
  fileExtension: string
  /** False until a person records that they checked it against a bank's own document. */
  verifiedAgainstBankSpec: boolean
  verifiedBy: string | null
  verifiedAt: string | null
  verificationNote: string | null
  layoutVersion: number
  active: boolean
  fieldCount: number
  /** What the screen must say while the layout is unverified. Null once it is. */
  verificationCaveat: string | null
}

export interface WpsLayoutFieldView {
  id: string
  layoutId: string
  fieldOrder: number
  name: string
  sourceKey: string
  literalValue: string | null
  fieldLength: number | null
  padCharacter: string | null
  padSide: string | null
  formatPattern: string | null
  decimalPlaces: number | null
  uppercase: boolean
  mandatory: boolean
  notes: string | null
}

export interface WpsFieldSourceView {
  key: string
  description: string
}

export interface PaymentBatchView {
  id: string
  legalEntityId: string
  establishmentId: string | null
  payrollRunId: string | null
  reference: string
  paymentMethod: string
  wpsLayoutId: string | null
  wpsLayoutVersion: number | null
  periodStart: string
  periodEnd: string
  valueDate: string | null
  currency: string
  totalAmount: number
  lineCount: number
  status: string
  generatedFileId: string | null
  generatedAt: string | null
  sentAt: string | null
  bankReference: string | null
  failureReason: string | null
  /** People in the run this batch could not pay. Never silently dropped. */
  excludedCount: number
}

export interface PaymentBatchLineView {
  id: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  lineNumber: number
  amount: number
  currency: string
  iban: string | null
  bankName: string | null
  routingCode: string | null
  wpsPersonId: string | null
  daysInPeriod: number | null
  /** Why this person is not in the file. Null when they are. */
  excludedReason: string | null
}

export interface SettlementConfigView {
  id: string
  legalEntityId: string
  daysPerYearFirstBand: number
  daysPerYearLaterBand: number
  bandBoundaryYears: number
  capInYearsOfWage: number | null
  minimumServiceMonths: number
  daysInMonthDivisor: number
  reduceOnResignation: boolean
  legacyLimitedContractLadder: boolean
  includeUnusedLeave: boolean
  includeAirTicket: boolean
  includeNoticePay: boolean
  recoverOutstandingLoans: boolean
  recoverUnreturnedAssets: boolean
  /**
   * BOOK_VALUE or RECOVERY_VALUE. Null means unreturned assets are named on the settlement
   * and charged nothing — there is no defensible default price for a used laptop.
   */
  assetRecoveryValuation: string | null
  statutoryReference: string | null
  notes: string | null
  /** True while nothing has been changed from Decree-Law 33/2021. */
  statutoryDefault: boolean
}

export interface SettlementLineView {
  id: string
  lineOrder: number
  component: string
  label: string
  direction: string
  quantity: number | null
  rate: number | null
  amount: number
  currency: string
  /** The words that produced this figure. */
  basis: string | null
  /** Why this component has no figure at all. Different from zero. */
  unavailableReason: string | null
  sourceReference: string | null
}

export interface FinalSettlementView {
  id: string
  legalEntityId: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  reference: string
  leavingReason: string
  joinDate: string
  lastWorkingDate: string
  noticeStartDate: string | null
  noticeEndDate: string | null
  noticeServed: boolean | null
  serviceYears: number
  serviceMonths: number
  serviceDays: number
  basicWageUsed: number | null
  basicWageBasis: string | null
  dailyWageUsed: number | null
  currency: string
  grossAmount: number
  deductionAmount: number
  netAmount: number
  status: string
  calculatedAt: string | null
  calculatedBy: string | null
  approvedAt: string | null
  approvedBy: string | null
  paidAt: string | null
  paymentBatchId: string | null
  statementFileId: string | null
  configSnapshot: string | null
  remarks: string | null
  lines: SettlementLineView[]
  unavailableComponentCount: number
}

export interface RegisterColumn {
  elementId: string
  code: string
  name: string
  deduction: boolean
}

export interface RegisterRow {
  employeeId: string
  employeeCode: string | null
  employeeName: string | null
  amounts: number[]
  netPay: number
}

export interface PayrollRegisterView {
  runId: string
  periodStart: string
  periodEnd: string
  status: string
  columns: RegisterColumn[]
  rows: RegisterRow[]
  columnTotals: number[]
  netTotal: number
}

export interface BankSummaryRow {
  bankName: string
  routingCode: string | null
  payments: number
  total: number
}

export interface BankSummaryView {
  batchId: string
  rows: BankSummaryRow[]
  total: number
  excludedCount: number
  excludedTotal: number
  /** Set when the batch pays fewer people than the run calculated. */
  exclusionNotice: string | null
}

export interface ProvisionMovementRow {
  provisionType: string
  employees: number
  opening: number
  accrued: number
  utilised: number
  closing: number
  basis: string | null
}

export interface GpssaReportRow {
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  nationality: string | null
  contributoryWage: number
  employeeAmount: number
  employerAmount: number
  governmentAmount: number
}

export interface GpssaReportView {
  year: number
  month: number
  rows: GpssaReportRow[]
  employeeTotal: number
  employerTotal: number
  governmentTotal: number
  /** Set when no configuration is in force, rather than showing an empty table. */
  unavailableReason: string | null
}
